/**
 * @file AvitoDashboardPage.tsx
 * @description Главная Avito-кабинета: статус подключения, счётчики, последняя
 * синхра, кнопки ручного триггера (phase14.0).
 *
 * Empty-state даёт явные инструкции для пользователя:
 *   1. Если интеграции нет → кнопка-CTA «Открыть настройки интеграции»
 *      с инструкцией где взять client_id / client_secret.
 *   2. Если интеграция настроена → счётчики + кнопки ручного триггера.
 *
 * Mobile-first: 1 колонка на <md, 3 колонки на ≥sm.
 */

import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Lock,
  Loader2,
  PlugZap,
  RefreshCw,
  Settings,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { Alert } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  shopAvito,
  type AvitoStatusDto,
  type AvitoSyncKind,
} from "../../../lib/api-shop-avito";

export function AvitoDashboardPage() {
  const notify = useNotify();
  const [status, setStatus] = useState<AvitoStatusDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<AvitoSyncKind | null>(null);
  const [testing, setTesting] = useState(false);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      setStatus(await shopAvito.status());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить статус");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function testConnection(): Promise<void> {
    setTesting(true);
    try {
      const r = await shopAvito.testConnection();
      if (r.ok) {
        notify.success(r.message);
        await refresh();
      } else {
        notify.error(r.message);
      }
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось проверить подключение");
    } finally {
      setTesting(false);
    }
  }

  async function trigger(kind: AvitoSyncKind): Promise<void> {
    setBusy(kind);
    try {
      const r = await shopAvito.triggerSync(kind);
      if (r.status === "success") {
        notify.success(`Синхронизация «${kindLabel(kind)}» завершена`);
      } else {
        notify.error(`Синхронизация «${kindLabel(kind)}» завершена с ошибкой`);
      }
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось запустить синхру");
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-on-surface-variant">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (!status) {
    // Очень редкий случай — apiFetch вернул null. Покажем понятный empty.
    return (
      <Alert severity="warning">Статус не получен. Попробуйте обновить страницу.</Alert>
    );
  }

  // ── Read-only banner: всегда виден на Dashboard ─────────────────────────
  const readOnlyBanner = (
    <Alert severity="info" icon={<Lock className="size-4" />}>
      <p className="font-medium">Режим только-чтение. Данные с Avito не изменяются.</p>
      <p className="text-on-surface-variant text-xs mt-1">
        На phase14.0 синхронизация работает в одну сторону: Avito → Chaiberi. Мы только читаем
        ваши объявления, заказы и отзывы. Никаких изменений в карточках на Avito этот модуль
        не делает (write-API появится в phase14.1).
      </p>
    </Alert>
  );

  // ── Empty-state: интеграция не настроена ────────────────────────────────
  if (!status.configured) {
    return (
      <div className="flex flex-col gap-4">
        {readOnlyBanner}
        <EditorialCard>
        <EditorialCard.Body className="space-y-4 text-center py-8">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-warning/15">
            <AlertTriangle className="size-6 text-warning" />
          </div>
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-on-surface">Avito не подключен</h2>
            <p className="text-sm text-on-surface-variant max-w-md mx-auto">
              Для синхронизации каталога-листингов, заказов Avito-Доставки и отзывов
              нужно добавить интеграцию с Avito API (OAuth2 client_credentials).
            </p>
          </div>
          <div className="text-left max-w-md mx-auto text-sm text-on-surface-variant space-y-2 bg-surface-low rounded-lg p-4">
            <p className="font-medium text-on-surface">Что нужно сделать:</p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>
                Зайти в кабинет продавца на{" "}
                <a
                  href="https://www.avito.ru/profile/settings/api"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  Avito → Настройки → API <ExternalLink className="size-3" />
                </a>
              </li>
              <li>Создать приложение, получить Client ID и Client Secret.</li>
              <li>
                Открыть платформенные интеграции и добавить запись типа AVITO.
              </li>
              <li>Вернуться сюда и нажать «Синхронизировать сейчас».</li>
            </ol>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
            <Button asChild>
              <Link to="/platform/integrations">
                <Settings className="size-4" />
                Открыть настройки интеграции
              </Link>
            </Button>
            <Button onClick={refresh} variant="ghost">
              <RefreshCw className="size-4" />
              Я уже настроил, проверить
            </Button>
          </div>
        </EditorialCard.Body>
      </EditorialCard>
      </div>
    );
  }

  // ── Основной вид: подключено ───────────────────────────────────────────
  return (
    <div className="flex flex-col gap-6">
      {readOnlyBanner}
      <EditorialCard>
        <EditorialCard.Body className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="size-5 text-primary" />
            <div>
              <div className="font-medium text-on-surface">Подключено к Avito</div>
              <div className="text-xs text-on-surface-variant">
                Учётные данные сохранены в платформенных интеграциях.
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={testConnection} disabled={testing}>
              {testing ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <PlugZap className="size-4" />
              )}
              Проверить подключение
            </Button>
            <Button variant="ghost" size="sm" onClick={refresh}>
              <RefreshCw className="size-4" />
              Обновить
            </Button>
          </div>
        </EditorialCard.Body>
      </EditorialCard>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <CountTile label="Листинги" value={status.counts.listings} to="listings" />
        <CountTile label="Заказы Avito" value={status.counts.orders} to="orders" />
        <CountTile label="Отзывы" value={status.counts.reviews} to="reviews" />
      </div>

      {status.lastJob && (
        <EditorialCard>
          <EditorialCard.Header title="Последняя синхронизация" />
          <EditorialCard.Body className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-on-surface-variant">Тип</div>
              <div className="font-medium text-on-surface">{kindLabel(status.lastJob.kind)}</div>
            </div>
            <div>
              <div className="text-on-surface-variant">Статус</div>
              <Badge
                variant={
                  status.lastJob.status === "success"
                    ? "success"
                    : status.lastJob.status === "error"
                      ? "destructive"
                      : "default"
                }
              >
                {statusLabel(status.lastJob.status)}
              </Badge>
            </div>
            <div>
              <div className="text-on-surface-variant">Обработано</div>
              <div className="font-medium text-on-surface">{status.lastJob.itemsProcessed}</div>
            </div>
            {status.lastJob.errorMessage && (
              <div className="md:col-span-3 text-destructive">{status.lastJob.errorMessage}</div>
            )}
          </EditorialCard.Body>
        </EditorialCard>
      )}

      <EditorialCard>
        <EditorialCard.Header
          title="Ручная синхронизация"
          subtitle="Запросить свежие данные с Avito прямо сейчас. По расписанию синхра идёт автоматически."
        />
        <EditorialCard.Body className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <SyncButton
            label="Листинги"
            description="Каталог объявлений (~1 час)"
            disabled={busy !== null}
            busy={busy === "listings"}
            onClick={() => trigger("listings")}
          />
          <SyncButton
            label="Заказы"
            description="Заказы Avito-Доставки (~30 мин)"
            disabled={busy !== null}
            busy={busy === "orders"}
            onClick={() => trigger("orders")}
          />
          <SyncButton
            label="Отзывы"
            description="Отзывы покупателей (~сутки)"
            disabled={busy !== null}
            busy={busy === "reviews"}
            onClick={() => trigger("reviews")}
          />
        </EditorialCard.Body>
      </EditorialCard>
    </div>
  );
}

function CountTile({ label, value, to }: { label: string; value: number; to: string }) {
  return (
    <Link to={to} className="block">
      <EditorialCard className="h-full transition-shadow hover:shadow">
        <EditorialCard.Body className="space-y-1">
          <div className="text-sm text-on-surface-variant">{label}</div>
          <div className="text-3xl font-semibold text-on-surface">{value}</div>
        </EditorialCard.Body>
      </EditorialCard>
    </Link>
  );
}

function SyncButton({
  label,
  description,
  busy,
  disabled,
  onClick,
}: {
  label: string;
  description: string;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      variant="outline"
      className="h-auto py-3 flex-col items-start gap-1"
    >
      <span className="inline-flex items-center gap-2 font-medium">
        {busy ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
        {label}
      </span>
      <span className="text-xs text-on-surface-variant font-normal">{description}</span>
    </Button>
  );
}

function kindLabel(kind: string): string {
  switch (kind) {
    case "listings":
      return "Листинги";
    case "orders":
      return "Заказы";
    case "reviews":
      return "Отзывы";
    default:
      return kind;
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case "success":
      return "Успех";
    case "error":
      return "Ошибка";
    case "running":
      return "Выполняется";
    default:
      return status;
  }
}
